import React from "react";
import { createTheme } from "@material-ui/core/styles";
import { BrandsEnum } from "../enums";
import * as ToggleButtons from "./common/ToggleButtonCommonBase";
import * as Helpers from "./common/Helpers";
import * as DatePicker from "./common/DateTimePickerCommonBase";
import { PositionProperty, TextTransformProperty } from "csstype";

// create default theme to get breakpoints
const theme = createTheme();
const colorCard = {};

const variables = {
  common: {
    borderRadius: 0,
  },
  colors: {
    primary: {
      main: "#05141F",
      dark: "#000000",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#FFFFFF",
      dark: "#000000",
      contrastText: "#FFFFFF",
    },
    error: {
      main: "#C82E2D",
    },
    info: {
      main: "#00AAE6",
      light: "#D6F1FB",
    },
    text: {
      primary: "#37434B",
      secondary: "#05141F",
      hint: "#808890",
    },

    background: "#FFFFFF",
    backgroundHeader: "#37434B",
    border: "#9BA1A5",
    hover: "#DEE1E3",
    placeholder: "#37434B",
    transparent: "#00000000",
    divider: "#F7F7F8",
    selectedBackground: "#F7F7F8",
    hoverBorder: "rgba(15,15,15,0.6)",
    hoverButton: "#f08d5f",
  },
};
const svgIcons = {
  circleRemove: {
    d: "path('M7 11v2h10v-2H7zm5-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z')",
  },
};

const defaultPathParams: React.SVGProps<SVGPathElement> = {
  stroke: "currentColor",
  strokeWidth: "1.5",
  fill: "none",
};

function getFontFamily(mainFont: string): string {
  return [mainFont ?? "", "Roboto", '"Helvetica Neue"', "Arial", "sans-serif"].join(",");
}

const props = Helpers.PrepareProperties(variables);

// More properties here: https://material-ui.com/customization/default-theme/
export const kia = {
  themeName: "Kia",
  brand: BrandsEnum.Kia,
  colorCard: colorCard,
  compositionBoxWidth: 816,
  containerMaxWidth: 412,
  pageMaxWidth: 1208,
  centred: "0 auto",
  border: "1px solid ",
  shape: {
    borderRadius: variables.common.borderRadius,
  },
  palette: {
    primary: variables.colors.primary,
    secondary: variables.colors.secondary,
    error: variables.colors.error,
    text: variables.colors.text,
    backgroundSecondary: {
      default: variables.colors.backgroundHeader,
      paper: variables.colors.selectedBackground,
    },
    background: {
      default: variables.colors.background,
      paper: variables.colors.selectedBackground,
    },
    divider: variables.colors.divider,
    grey: {
      400: variables.colors.border,
    },
    disabledColor: variables.colors.placeholder,
    info: variables.colors.info,
  },
  typography: {
    fontFamily: getFontFamily("KiaSignatureSweden"),
    color: variables.colors.text.primary,
    h1: {
      fontSize: "2rem",
      lineHeight: "2.25rem",
      color: variables.colors.text.secondary,
      fontWeight: 900,
      [theme.breakpoints.up("md")]: {
        fontSize: "2.5rem",
        lineHeight: "3rem",
      },
      textAlign: "center" as "center",
    },
    h2: {
      fontSize: "2rem",
      lineHeight: "2.25rem",
      color: variables.colors.text.secondary,
      fontWeight: 900,
      textAlign: "center" as "center",
      [theme.breakpoints.up("md")]: {
        fontSize: "2rem",
        lineHeight: "2.5rem",
      },
      paddingBottom: theme.spacing(4),
    },
    h3: {
      fontSize: "1.25rem",
      lineHeight: "2.25rem",
      color: variables.colors.primary.main,
      fontWeight: 900,
      textDecoration: "underline",
    },
    h4: {
      color: variables.colors.text.primary,
      fontWeight: 900,
      fontSize: "1.25rem",
      lineHeight: "1.75rem",
    },
    h5: {
      fontSize: "1.25rem",
      color: variables.colors.text.primary,
      fontWeight: 900,
    },
    h6: {
      color: variables.colors.secondary.main,
      fontSize: "1rem",
      lineHeight: "1.75rem",
    },
    // helper text
    caption: {
      fontSize: "1rem",
      lineHeight: "1.5rem",
    },
    button: {
      fontSize: "1rem",
      fontWeight: "normal" as "normal",
      lineHeight: "1.5rem",
      minHeight: 48,
    },
    subtitle2: {
      fontSize: "1rem",
      lineHeight: "1.5rem",
      color: variables.colors.text.primary,
      fontWeight: "normal" as "normal",
    },
    body1: {
      fontSize: "1rem",
      lineHeight: "1.5rem",
      color: variables.colors.text.primary,
    },
    body2: {
      fontSize: "1.2rem",
      lineHeight: "1.5rem",
      color: variables.colors.primary.main,
    },
  },
  radioButton: {
    borderRadius: variables.common.borderRadius,
    border: `1px solid ${variables.colors.border}`,
    activeBorder: `1px solid ${variables.colors.primary.main}`,
    activeBackgroundColor: "#FFFFFF",
    activeFontWeight: 500,
  },
  addOnCard: {
    component: {
      width: 380,
      display: "inline-block",
      marginBottom: "8px",
    },
    card: {
      border: `1px solid ${variables.colors.border}`,
      padding: "25px 34px",
    },
    cardSelected: {
      border: `1px solid ${variables.colors.primary.main}`,
      padding: "25px 34px",
      addText: {
        color: variables.colors.text.secondary,
        fontWeight: 900,
      },
    },
    header: {
      textColor: variables.colors.text.secondary,
      fontWeight: 900,
    },
    addText: {
      color: variables.colors.text.secondary,
      fontWeight: 900,
    },
    price: {
      color: variables.colors.text.secondary,
      fontWeight: 900,
    },
  },
  checkoutLinks: {
    color: variables.colors.primary.main,
  },
  overrides: {
    // Style sheet name. Look for specific component CSS API: https://material-ui.com/api/button/#css
    MuiTypography: {
      root: {
        textTransform: "none" as TextTransformProperty,
      },
    },
    MuiButton: {
      // Name of the rule
      root: {
        // Some CSS
        textTransform: "none" as TextTransformProperty,
      },
      textSecondary: {
        color: variables.colors.text.secondary,
        "&:hover": {
          textDecoration: "underline",
        },
      },
      contained: {
        boxShadow: props.buttons.boxShadow,
        borderRadius: 2,
        "&:hover": {
          boxShadow: props.buttons.boxShadow,
        },
        "&:active": {
          boxShadow: props.buttons.boxShadow,
        },
        "&$focusVisible": {
          boxShadow: props.buttons.boxShadow,
        },
      },
      containedPrimary: {
        backgroundColor: variables.colors.primary.main,
        fontWeight: 400,
        color: variables.colors.primary.contrastText,
        textDecoration: "none",
        "&:hover": {
          border: "1px solid " + variables.colors.primary.main,
          color: variables.colors.secondary.contrastText,
          backgroundColor: variables.colors.primary.main,
          textDecoration: "underline",
          "& span": {
            textDecoration: "none",
          },
        },
      },
      containedSecondary: {
        "&:hover": {
          border: "2px solid " + variables.colors.primary.main,
          color: variables.colors.primary.dark,
          backgroundColor: variables.colors.transparent,
          textDecoration: "underline",
          "& span": {
            textDecoration: "none",
          },
        },
        "&:active": {
          border: "2px solid " + variables.colors.primary.main,
          color: variables.colors.primary.dark,
          backgroundColor: variables.colors.transparent,
          textDecoration: "underline",
          "& span": {
            textDecoration: "none",
          },
        },
        color: variables.colors.primary.main,
        backgroundColor: variables.colors.primary.dark,
        border: "2px solid " + variables.colors.primary.dark,
        // this is just to make link styling same as button if link has button classes
        // needed to override link styling by external CSS by making selector more specific
        "a&&": {
          "&:hover": {
            border: "2px solid " + variables.colors.primary.main,
            color: variables.colors.primary.dark,
            backgroundColor: variables.colors.transparent,
            "& span": {
              textDecoration: "none",
            },
          },
          "&:active": {
            border: "2px solid " + variables.colors.primary.main,
            color: variables.colors.primary.dark,
            backgroundColor: variables.colors.transparent,
            "& span": {
              textDecoration: "none",
            },
          },
          color: variables.colors.primary.dark,
          backgroundColor: variables.colors.transparent,
          border: "2px solid " + variables.colors.primary.dark,
        },
      },
      outlinedSecondary: {
        "&:hover": {
          border: "2px solid " + variables.colors.primary.contrastText,
          color: variables.colors.primary.contrastText,
          backgroundColor: variables.colors.transparent,
          textDecoration: "underline",
        },
        "&:active": {
          border: "2px solid " + variables.colors.primary.contrastText,
          color: variables.colors.primary.contrastText,
          backgroundColor: variables.colors.transparent,
          textDecoration: "underline",
        },
        color: variables.colors.primary.contrastText,
        border: "2px solid " + variables.colors.primary.contrastText,
      },
      text: {
        "&:hover": {
          color: variables.colors.secondary.dark,
        },
        "&:active": {
          color: variables.colors.text.hint,
        },
        color: variables.colors.text.primary,
      },
      textPrimary: {
        color: variables.colors.primary.dark,
        "&:hover": {
          color: variables.colors.primary.main,
          backgroundColor: variables.colors.transparent,
        },
        "&:active": {
          backgroundColor: variables.colors.transparent,
        },
      },
      /* Styles applied to the icon element if supplied and `size="medium"`. */
      iconSizeMedium: {
        "& > *:first-child": {
          fontSize: 20,
        },
      },
    },
    MuiRadio: {
      root: {
        color: variables.colors.primary.main,
      },
      colorSecondary: {
        "&.Mui-checked": {
          color: variables.colors.primary.main,
        },
      },
    },
    MuiIconButton: {
      root: {
        color: variables.colors.primary.main,
        "&:hover": {
          color: variables.colors.primary.dark,
        },
      },
      sizeSmall: {
        padding: 0,
      },
    },
    MuiFormLabel: {
      root: {
        color: variables.colors.text.secondary,
        "&$focused": {
          color: variables.colors.border,
        },
        fontWeight: 400,
      },
    },
    MuiInputLabel: {
      formControl: {
        color: variables.colors.text.secondary,
        position: "relative" as PositionProperty,
        transform: "none",
        fontWeight: 400,
        lineHeight: "1.75rem", // safe to use InputLabel + <SvgIcon size='small' />
      },
    },
    MuiOutlinedInput: {
      root: {
        border: "1px solid " + variables.colors.border,
        backgroundColor: variables.colors.background,
        "&:hover": {
          backgroundColor: variables.colors.hover,
          "& $notchedOutline": {
            borderColor: variables.colors.border,
          },
          "&$focused $notchedOutline": {
            borderColor: variables.colors.primary.main,
          },
          "&$error $notchedOutline": {
            borderColor: variables.colors.error.main,
          },
        },
        "&$focused $notchedOutline": {
          borderColor: variables.colors.primary.main,
          borderWidth: 0,
        },
      },
      notchedOutline: {
        borderColor: variables.colors.border,
        borderWidth: 0,
      },
      input: {
        "&::-webkit-input-placeholder": props.inputs.placeholder,
        "&::-moz-placeholder": props.inputs.placeholder, // Firefox 19+
        "&:-ms-input-placeholder": props.inputs.placeholder, // IE 11
        "&::-ms-input-placeholder": props.inputs.placeholder, // Edge
      },
    },
    MuiFormHelperText: {
      root: {
        color: variables.colors.text.hint,
      },
      contained: {
        marginLeft: 0,
      },
    },
    MuiListItem: {
      ...props.listItems,
      button: {
        backgroundColor: variables.colors.background,
        "&:hover": {
          backgroundColor: variables.colors.hover,
        },
        "&$selected": {
          backgroundColor: variables.colors.background,
          "&:hover": {
            backgroundColor: variables.colors.hover,
          },
        },
      },
    },
    MuiMenuItem: {
      ...props.listItems,
    },
    MuiSelect: {
      select: {
        "&:focus": {
          backgroundColor: variables.colors.transparent,
        },
        // Setting icon with IconComponent property, looses all select styling, so we change SVG path directly to one we need
        "&~ svg": {
          "&> path": {
            d: "path('M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z')",
          },
        },
      },
      icon: {
        fill: variables.colors.primary.main,
      },
    },
    MuiList: {
      padding: {
        paddingTop: 0,
        paddingBottom: 0,
      },
    },
    MuiSvgIcon: {
      fontSizeInherit: {
        fontSize: "56px",
      },
    },
    MuiLink: {
      underlineHover: {
        "a&": {
          // to increase specificy
          color: variables.colors.text.secondary,
          "&:hover": {
            color: variables.colors.primary.main,
            textDecoration: "underline",
          },
          "&:active": {
            color: variables.colors.primary.main,
          },
        },
      },
    },
    MuiFormControlLabel: {
      label: {
        color: variables.colors.primary.main,
        fontWeight: 400,
      },
    },
    MuiToggleButtonGroup: {
      root: {
        ...ToggleButtons.CommonBase.MuiToggleButtonGroup.root,
        borderRadius: variables.common.borderRadius,
      },
      groupedHorizontal: {
        ...ToggleButtons.CommonBase.MuiToggleButtonGroup.groupedHorizontal,
        ...ToggleButtons.SetMuiToggleButtonGroupBorders({
          ...theme,
          shape: {
            borderRadius: variables.common.borderRadius,
          },
        }),
      },
    },
    MuiToggleButton: {
      root: {
        ...ToggleButtons.CommonBase.MuiToggleButton.root,
        color: variables.colors.placeholder,
        border: "1px solid " + variables.colors.border,
        fontWeight: 400,
        "&$selected": {
          color: variables.colors.primary.contrastText,
          backgroundColor: variables.colors.primary.main,
          borderColor: variables.colors.primary.main,
          "&:hover": {
            backgroundColor: variables.colors.hover,
            [theme.breakpoints.down("sm")]: {
              backgroundColor: variables.colors.primary.main,
            },
          },
        },
        "&:hover": {
          backgroundColor: variables.colors.hover,
          [theme.breakpoints.down("sm")]: {
            backgroundColor: variables.colors.primary.main,
          },
        },
      },
    },
    MuiPickersBasePicker: {
      container: {
        ...DatePicker.CommonBase.MuiPickersBasePicker.container,
      },
    },
    MuiCard: {
      root: {
        width: 385,
      },
    },
  },
  footerBar: {
    backgroundColor: variables.colors.backgroundHeader,
    textColor: variables.colors.secondary.contrastText,
  },
  priceText: {
    totalPriceColor: variables.colors.text.primary,
    fontWeight: 400,
  },
  header: {
    "& *": {
      color: variables.colors.primary.contrastText,
    },
  },
  popup: {
    root: {
      fontFamily: getFontFamily("KiaSignatureSweden"),
    },
  },
  uspContainerBlockWrapper: {
    backgroundColor: "#333333",
  },
  uspBlock: {
    header: {
      fontWeight: 500,
      color: "#FFFFFF",
      fontSize: "1.25rem",
      lineHeight: "1.75rem",
      fontFamily: getFontFamily("KiaSignatureSweden"),
      [theme.breakpoints.up("md")]: {
        fontSize: "1.5rem",
        lineHeight: "2rem",
      },
    },
    text: {
      fontWeight: 400,
      color: "#FFFFFF",
      fontSize: "1rem",
      lineHeight: "1.5rem",
    },
  },
  outlinedButton: {
    "& span": {
      color: variables.colors.primary.contrastText,
      width: "100%",
      height: "100%",
      minHeight: "56px",
    },
    "& span:hover": {
      color: variables.colors.primary.contrastText,
    },
    padding: "0",
    minHeight: "56px",
    display: "flex",
  },
  icons: {
    dateTimeIcon: (
      <path
        {...defaultPathParams}
        strokeLinecap='square'
        strokeMiterlimit='10'
        d='m 2.4615,5.7769 h 20.577 V 21.5526 H 2.4615 Z m 20.577,3.4295 H 2.4615 M 17.5513,2.3474 V 5.7769 M 7.9487,2.3474 v 3.4295'
      />
    ),
    infoIcon: (
      <path
        {...defaultPathParams}
        paintOrder='normal'
        strokeLinecap='butt'
        strokeMiterlimit='10'
        d='M 10.75,8.1503401 V 16.34966 m 10,-5.59966 c 0,5.522848 -4.477152,10 -10,10 -5.5228475,0 -10,-4.477152 -10,-10 0,-5.5228475 4.4771525,-10 10,-10 5.522848,0 10,4.4771525 10,10 M 11.402081,5.164 c 0,0.4120491 -0.334032,0.7460809 -0.746081,0.7460809 -0.412049,0 -0.7460809,-0.3340318 -0.7460809,-0.7460809 0,-0.4120491 0.3340319,-0.7460809 0.7460809,-0.7460809 0.412049,0 0.746081,0.3340318 0.746081,0.7460809'
      />
    ),
    closeIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M4 19.0797L11.0945 11.9852L4 4.89013L4.89073 4L11.9852 11.0945L19.0797 4L19.9704 4.86054L12.8759 11.9852L20 19.1093L19.1099 20L11.9852 12.8753L4.89073 19.9704L4 19.0797Z'
          fill='#1A1A1A'
        />
      </svg>
    ),
    helpIcon: (
      <path
        {...defaultPathParams}
        strokeDasharray='none'
        paintOrder='normal'
        strokeLinecap='butt'
        strokeMiterlimit='4'
        strokeOpacity='1'
        fillOpacity='1'
        d='m 9.938,6.526 c 1.861,-0.823 4.275,-0.727 5.1,0.607 0.825,1.334 0.255,2.883 -1.157,4.079 -1.412,1.196 -1.88,1.847 -1.88,2.788 m 9.999019,-2 c 0,5.522847 -4.477153,10 -10,10 -5.5228475,0 -10,-4.477153 -10,-10 0,-5.5228475 4.4771525,-10 10,-10 5.522847,0 10,4.4771525 10,10 m -10.00002,4.914346 c -0.410685,0 -0.75167,0.340985 -0.75167,0.75167 0,0.03362 0.0017,0.06624 0.0059,0.09836 v 0.0015 c 0.04876,0.367965 0.368545,0.651804 0.7458,0.651804 0.320751,0 0.600313,-0.204508 0.706158,-0.493283 0.02965,-0.08092 0.04551,-0.168432 0.04551,-0.258387 0,-0.05665 -0.0071,-0.112575 -0.01909,-0.165897 -0.07589,-0.336227 -0.378404,-0.585773 -0.73258,-0.585773 z'
      />
    ),
    checkmarkIconCSS: encodeURIComponent(`<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path fillRule="evenodd" clipRule="evenodd" d="M22 7.34858L10.2386 19.1287L2 10.89L4.34858 8.54152L10.2386 14.4129L19.6514 5L22 7.34858Z" fill="#05141F" />
</svg>`),
    backIcon: (
      <path
        {...defaultPathParams}
        stroke={variables.colors.primary.main}
        strokeLinecap='square'
        strokeMiterlimit='10'
        d='m 15,6 -6,6 6,6'
      />
    ),
    addIcon: <path {...defaultPathParams} strokeLinecap='square' strokeMiterlimit='10' d='M 20,12 H 4 m 8,-8 v 16' />,
    statusIndicatorIcon: <circle cx='6' cy='6' r='5' fill={variables.colors.info.main} stroke='white' strokeWidth='2' />,
  },
  messageBox: {
    backgroundColor: "#E3E9F5",
    borderColor: variables.colors.transparent,
    dividerColor: variables.colors.border,
    textAlign: "left",
  },
  coverageSelect: {
    root: {
      CoverageHeaderName: {
        color: variables.colors.text.secondary,
      },
      CoverageItemText: {
        color: variables.colors.text.secondary,
      },
      CoverageHeaderPrice: {
        color: variables.colors.text.secondary,
      },
    },
    selected: {
      backgroundColor: variables.colors.selectedBackground,
      textColor: variables.colors.text.secondary,
    },
    descriptionCard: {
      backgroundColor: variables.colors.selectedBackground,
      borderColor: variables.colors.selectedBackground,
      textColor: variables.colors.text.primary,
    },
  },
  CheckoutPage: {
    priceText: {
      color: variables.colors.primary.contrastText,
    },
    bottomTotalPriceText: {
      color: variables.colors.primary.main,
    },
  },
  ConfirmationPage: {
    ConfirmationButtonOutlined: {
      color: variables.colors.primary.dark,
    },
  },
  confirmationPageHeader: {
    color: variables.colors.primary.contrastText,
  },
  skeletonHeaderText: {
    background: theme.palette.secondary.contrastText,
  },
  paymentOption: {
    label: {
      lineHeight: "1.75rem",
      color: variables.colors.text.secondary,
    },
    color: variables.colors.text.secondary,
  },
  payment: {
    backgroundColor: "#FFFFFF",
  },
  stickyPriceButton: {
    backgroundColor: variables.colors.primary.main,
    hoverColor: variables.colors.primary.dark,
    borderColor: variables.colors.primary.main,
    textColor: variables.colors.primary.contrastText,
  },
};

export default createTheme(kia);
